import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import StorefrontCancelAppointmentDialog from '../../../components/StorefrontCancelAppointmentDialog';
import { useNavigate, useParams } from 'react-router-dom';
import CustomButton from '../cssbutton/CustomButton';
import {
  canClientCancelAppointment,
  getStorefrontAppointmentCancelConfirmationPath,
  resolveClientPortalCompanyId,
  resolveClientPortalCompanyUrl,
} from '../../../utils/clientPortalPaths';
import {
  canCheckInAppointment,
  isAwaitingClientConfirmation,
  isCheckedIn,
} from '../../../../../utils/appointmentWorkflow';
import {
  cancelStorefrontAppointment,
  checkInStorefrontAppointment,
  confirmStorefrontAppointment,
} from '../../../../../api/storefront/client';
import './styles/appointment-actions/AppointmentActions.css';

const AppointmentActions = ({ 
  appointment, 
  companyId: companyIdProp,
  companySlug: companySlugProp,
  onActionComplete, 
  isProcessing, 
  setIsProcessing, 
  setError,
  onReschedule,
  bookingSettings = {},
  sx = {} // Add sx prop for flexible styling
}) => {
  const navigate = useNavigate();
  const { companyUrl: companyUrlParam } = useParams();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isCheckInDialogOpen, setIsCheckInDialogOpen] = useState(false);
  const awaitingClientConfirmation = isAwaitingClientConfirmation(appointment);
  const checkInState = canCheckInAppointment(appointment, bookingSettings, { actor: 'client' });
  const appointmentIsCheckedIn = isCheckedIn(appointment);
  const [selectedReason, setSelectedReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState('');

  const handleCancelAppointment = async () => {
    try {
      // Validate cancel reason
      if (!selectedReason) {
        setCancelReasonError('Please select a reason for cancellation');
        return;
      }

      if (selectedReason === 'Other' && !otherReason.trim()) {
        setCancelReasonError('Please provide details for your cancellation reason');
        return;
      }

      setIsProcessing(true);
      const resolvedCompanyUrl = resolveClientPortalCompanyUrl({
        companyUrl: companySlugProp || companyUrlParam,
        appointment,
      });
      const companyId = companyIdProp
        || appointment?.companyId
        || await resolveClientPortalCompanyId({ appointment, companyUrl: resolvedCompanyUrl });

      if (!companyId || !appointment?.id) {
        throw new Error('Missing required information');
      }

      if (!canClientCancelAppointment(appointment)) {
        throw new Error('This appointment cannot be cancelled online. Please contact the business.');
      }

      const finalReason = selectedReason === 'Other' ? otherReason.trim() : selectedReason;

      const updatedData = {
        status: 'cancelled',
        cancelReason: finalReason,
        cancelledBy: 'client',
      };

      await cancelStorefrontAppointment({
        companyId,
        appointmentId: appointment.id,
        cancelReason: finalReason,
      });

      setSelectedReason('');
      setOtherReason('');
      setCancelReasonError('');
      setIsCancelDialogOpen(false);

      navigate(getStorefrontAppointmentCancelConfirmationPath(resolvedCompanyUrl, appointment), {
        state: {
          appointmentData: {
            ...appointment,
            ...updatedData,
          },
        },
      });
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      const message = error?.message || error?.code || '';
      setError(
        message.includes('permission')
          ? 'You do not have permission to cancel this appointment. Sign in with the email used when booking.'
          : 'Failed to cancel appointment. Please try again.'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmAppointment = async () => {
    try {
      setIsProcessing(true);
      const companyId = companyIdProp
        || appointment?.companyId
        || await resolveClientPortalCompanyId({
          appointment,
          companyUrl: companySlugProp || companyUrlParam,
        });

      if (!companyId || !appointment?.id) {
        throw new Error('Missing required information');
      }

      if (appointment.isArchived) {
        throw new Error('Archived appointments cannot be updated online.');
      }

      await confirmStorefrontAppointment({
        companyId,
        appointmentId: appointment.id,
      });

      // Close dialog
      setIsConfirmDialogOpen(false);
      
      // Notify parent component to refresh data
      if (onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      console.error('Error confirming appointment:', error);
      setError('Failed to confirm appointment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCheckInAppointment = async () => {
    try {
      setIsProcessing(true);
      const companyId = companyIdProp
        || appointment?.companyId
        || await resolveClientPortalCompanyId({
          appointment,
          companyUrl: companySlugProp || companyUrlParam,
        });

      if (!companyId || !appointment?.id) {
        throw new Error('Missing required information');
      }

      const response = await checkInStorefrontAppointment({
        companyId,
        appointmentId: appointment.id,
      });
      const ok = response?.success !== false;

      if (!ok) {
        throw new Error(response?.message || 'Check-in failed');
      }

      setIsCheckInDialogOpen(false);
      if (onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      console.error('Error checking in:', error);
      setError(error.message || 'Failed to check in. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const showCancel = canClientCancelAppointment(appointment);
  const showConfirm = awaitingClientConfirmation
    && appointment.status !== 'cancelled'
    && appointment.status !== 'completed';
  const showCheckIn = checkInState.eligible && !appointmentIsCheckedIn
    && appointment.status !== 'cancelled'
    && appointment.status !== 'completed';
  const showReschedule = showCancel;
  const showActions = showCancel || showConfirm || showCheckIn || showReschedule;

  return (
    <>
      {/* Action Buttons */}
      {showActions && (
        <div className="appointment-quick-actions" style={sx}>
          {showCancel && (
          <button
            type="button"
            className="appointment-quick-action appointment-quick-action-cancel"
            onClick={() => setIsCancelDialogOpen(true)}
            disabled={isProcessing}
            aria-label="Cancel appointment"
          >
            <span className="appointment-quick-action-label">Cancel</span>
          </button>
          )}

          {showConfirm && (
            <button
              type="button"
              className="appointment-quick-action appointment-quick-action-confirm"
              onClick={() => setIsConfirmDialogOpen(true)}
              disabled={isProcessing}
              aria-label="Confirm appointment"
            >
              <span className="appointment-quick-action-label">Confirm</span>
            </button>
          )}

          {showCheckIn && (
            <button
              type="button"
              className="appointment-quick-action appointment-quick-action-confirm"
              onClick={() => setIsCheckInDialogOpen(true)}
              disabled={isProcessing}
              aria-label="Check in for appointment"
            >
              <span className="appointment-quick-action-label">Check In</span>
            </button>
          )}

          {showReschedule && (
          <button
            type="button"
            className="appointment-quick-action appointment-quick-action-reschedule"
            onClick={() => !isProcessing && onReschedule && onReschedule()}
            disabled={isProcessing}
            aria-label="Reschedule appointment"
          >
            <span className="appointment-quick-action-label">Reschedule</span>
          </button>
          )}
        </div>
      )}

      <StorefrontCancelAppointmentDialog
        open={isCancelDialogOpen}
        isProcessing={isProcessing}
        selectedReason={selectedReason}
        otherReason={otherReason}
        cancelReasonError={cancelReasonError}
        onSelectReason={(reason) => {
          setSelectedReason(reason);
          setCancelReasonError('');
        }}
        onOtherReasonChange={(value) => {
          setOtherReason(value);
          if (value.trim()) {
            setCancelReasonError('');
          }
        }}
        onClose={() => {
          if (!isProcessing) {
            setIsCancelDialogOpen(false);
            setSelectedReason('');
            setOtherReason('');
            setCancelReasonError('');
          }
        }}
        onConfirm={handleCancelAppointment}
      />

      {/* Confirm Dialog */}
      <Dialog
        open={isConfirmDialogOpen}
        onClose={() => !isProcessing && setIsConfirmDialogOpen(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          Confirm Appointment
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Are you sure you want to confirm this appointment?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CustomButton 
            onClick={() => setIsConfirmDialogOpen(false)}
            disabled={isProcessing}
            variant="secondary"
            size="medium"
          >
            No, Not Yet
          </CustomButton>
          <CustomButton 
            onClick={handleConfirmAppointment}
            variant="success"
            disabled={isProcessing}
            size="medium"
            className={isProcessing ? 'custom-button--loading' : ''}
          >
            {isProcessing ? 'Confirming...' : 'Yes, Confirm It'}
          </CustomButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isCheckInDialogOpen}
        onClose={() => !isProcessing && setIsCheckInDialogOpen(false)}
        aria-labelledby="check-in-dialog-title"
        aria-describedby="check-in-dialog-description"
      >
        <DialogTitle id="check-in-dialog-title">
          Check In
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="check-in-dialog-description">
            Check in for your appointment now?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setIsCheckInDialogOpen(false)}
            disabled={isProcessing}
            variant="secondary"
            size="medium"
          >
            Not Yet
          </CustomButton>
          <CustomButton
            onClick={handleCheckInAppointment}
            variant="success"
            disabled={isProcessing}
            size="medium"
            className={isProcessing ? 'custom-button--loading' : ''}
          >
            {isProcessing ? 'Checking in…' : 'Check In'}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppointmentActions; 